import React, { useState } from 'react';

import { faClose } from '@fortawesome/pro-light-svg-icons';

import { DARK } from 'constants/colors';

import {
  Box,
  Drawer,
  DrawerProps,
  IconButton,
  Typography
} from '@mui/material';

import FontAwesomeSvgIcon from '../FontAwesomeSvgIcon';
import { SxProps } from '@mui/material/styles';
import { WarningDialog } from 'components/roster-portal/activity-planning/components';
import { DrawerContext } from './DrawerContext';
import { useTranslation } from 'react-i18next';

interface DrawerTitleProps {
  title: string | React.ReactNode;
  onClose: () => void;
}

function DrawerTitle({ title, onClose }: DrawerTitleProps) {
  return (
    <Box
      pb={2}
      pt={3.5}
      px={2.5}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      borderBottom="1px solid #E0E0E0"
    >
      <Typography
        sx={{
          fontFamily: 'signikalight',
          fontSize: 18
        }}
      >
        {title}
      </Typography>
      <IconButton
        onClick={onClose}
        sx={{
          ml: 4
        }}
      >
        <FontAwesomeSvgIcon
          icon={faClose}
          sx={{
            color: DARK
          }}
        />
      </IconButton>
    </Box>
  );
}

interface DrawerContentProps {
  children: React.ReactNode;
  sx?: SxProps;
}

function DrawerContent({ children, sx }: DrawerContentProps) {
  return <Box sx={sx}>{children}</Box>;
}

export type CustomDrawerProps = Omit<
  DrawerProps,
  'title' | 'children' | 'onClose' | 'sx'
> &
  DrawerTitleProps &
  DrawerContentProps;

export default function CustomDrawer({
  anchor = 'right',
  open,
  children,
  onClose,
  title,
  sx,
  ...props
}: CustomDrawerProps) {
  const { t } = useTranslation('common');
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasDirtyChildForm, sethasDirtyChildForm] = useState(false);

  const onCloseConfirmation = () => {
    if (hasDirtyChildForm) setShowCloseConfirmation(true);
    else {
      onClose?.();
    }
  };

  const handleClose = () => {
    setShowCloseConfirmation(false);
    sethasDirtyChildForm(false);
    onClose?.();
  };

  return (
    <DrawerContext.Provider
      value={{
        onInnerFormChange: (formState) => {
          sethasDirtyChildForm(formState.isDirty);
        }
      }}
    >
      {/* CONFIRMATION WARNING */}
      <WarningDialog
        title={t('unsavedChanges')}
        onCancel={() => setShowCloseConfirmation(false)}
        onSubmit={handleClose}
        open={showCloseConfirmation}
      />

      {/* DIALOG */}
      <Drawer
        anchor={anchor}
        open={open}
        onClose={onCloseConfirmation}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        <DrawerTitle title={title} onClose={onCloseConfirmation} />
        <DrawerContent sx={sx}>{children}</DrawerContent>
      </Drawer>
    </DrawerContext.Provider>
  );
}
